import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Building2, Users, Calendar, Clock, Plus, Eye, TrendingUp, Award, ArrowRight, Copy, Send, FileText, Mail, MessageSquare, Phone, StickyNote, History, FileDown, Trash2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['New', 'Outreach Sent', 'Contacted', 'Confirmed', 'Completed', 'Sponsor', 'Long-Term Partner'];
const TIER_OPTIONS = ['Prospect', 'Bronze Partner', 'Silver Partner', 'Gold Partner'];
const ACTIVITY_TYPES = [
  "Bookshelf Assembly Day",
  "Bookshelf Painting & Design",
  "Bookshelf Delivery Day",
  "Book Sorting & Packing",
  "Literacy Event Support"
];

const ACTIVITY_LOG_TYPES = [
  { value: 'outreach_sent', label: 'Outreach Sent', icon: Send },
  { value: 'follow_up', label: 'Follow-Up', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Users },
  { value: 'phone_call', label: 'Phone Call', icon: Phone },
  { value: 'note', label: 'Note', icon: StickyNote }
];

export default function CorporateDashboard() {
  const [partners, setPartners] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showPartnerDetailDialog, setShowPartnerDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailPreview, setEmailPreview] = useState({ subject: '', body: '' });
  const [activityForm, setActivityForm] = useState({ activity_type: '', description: '' });
  const [partnerActivities, setPartnerActivities] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [eventForm, setEventForm] = useState({
    corporate_id: '',
    company_name: '',
    title: '',
    description: '',
    activity_type: '',
    date: '',
    start_time: '09:00',
    end_time: '13:00',
    location: 'The Bookshelf Project Workshop',
    volunteer_capacity: 25,
    bookshelves_goal: 0
  });

  useEffect(() => {
    fetchData();
    fetchEmailTemplates();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, eventsRes, statsRes] = await Promise.all([
        fetch(`${API}/corporate/partners`, { credentials: 'include' }),
        fetch(`${API}/corporate/events`, { credentials: 'include' }),
        fetch(`${API}/corporate/stats`, { credentials: 'include' })
      ]);

      if (partnersRes.ok) setPartners(await partnersRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${API}/corporate/email-templates`, { credentials: 'include' });
      if (response.ok) {
        setEmailTemplates(await response.json());
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchPartnerActivities = async (corporateId) => {
    try {
      const response = await fetch(`${API}/corporate/partners/${corporateId}/activities`, { credentials: 'include' });
      if (response.ok) {
        setPartnerActivities(await response.json());
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const updatePartnerStatus = async (corporateId, field, value) => {
    try {
      const response = await fetch(`${API}/corporate/partners/${corporateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        toast.success('Partner updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update partner');
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/corporate/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(eventForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Event created!');
        
        // Show registration link
        const registrationUrl = `${window.location.origin}/corporate/register/${data.registration_token}`;
        navigator.clipboard.writeText(registrationUrl);
        toast.info('Registration link copied to clipboard!');
        
        setShowEventDialog(false);
        fetchData();
        setEventForm({
          corporate_id: '',
          company_name: '',
          title: '',
          description: '',
          activity_type: '',
          date: '',
          start_time: '09:00',
          end_time: '13:00',
          location: 'The Bookshelf Project Workshop',
          volunteer_capacity: 25,
          bookshelves_goal: 0
        });
      } else {
        toast.error('Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const openEventDialogForPartner = (partner) => {
    setSelectedPartner(partner);
    setEventForm(prev => ({
      ...prev,
      corporate_id: partner.corporate_id,
      company_name: partner.company_name,
      title: `${partner.company_name} Volunteer Day`,
      description: `Corporate volunteer day for ${partner.company_name} team.`,
      activity_type: partner.preferred_activities?.[0] || 'Bookshelf Assembly Day',
      volunteer_capacity: partner.estimated_volunteers || 25
    }));
    setShowEventDialog(true);
  };

  const copyRegistrationLink = (token) => {
    const url = `${window.location.origin}/corporate/register/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Registration link copied!');
  };

  const sendToolkit = async (partner) => {
    try {
      const response = await fetch(`${API}/corporate/send-toolkit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          corporate_id: partner.corporate_id,
          email: partner.contact_email,
          contact_name: partner.contact_name,
          company_name: partner.company_name
        })
      });

      if (response.ok) {
        toast.success(`Toolkit sent to ${partner.contact_email}!`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send toolkit');
      }
    } catch (error) {
      toast.error('Failed to send toolkit');
    }
  };

  const sendInfoPackage = async (partner) => {
    try {
      const response = await fetch(`${API}/corporate/send-info-package`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          corporate_id: partner.corporate_id,
          email: partner.contact_email,
          contact_name: partner.contact_name,
          company_name: partner.company_name,
          preferred_activities: partner.preferred_activities || [],
          preferred_date_range: partner.preferred_date_range || '',
          preferred_time: partner.preferred_time || '',
          estimated_volunteers: partner.estimated_volunteers || 0
        })
      });

      if (response.ok) {
        toast.success(`Personalized Info Package sent to ${partner.contact_email}!`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send info package');
      }
    } catch (error) {
      toast.error('Failed to send info package');
    }
  };

  const openEmailDialog = (partner) => {
    setSelectedPartner(partner);
    setSelectedTemplate('');
    setEmailPreview({ subject: '', body: '' });
    setShowEmailDialog(true);
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId && emailTemplates[templateId] && selectedPartner) {
      let subject = emailTemplates[templateId].subject;
      let body = emailTemplates[templateId].body;
      
      // Replace variables
      const variables = {
        contact_name: selectedPartner.contact_name,
        company_name: selectedPartner.company_name,
        sender_name: 'The Bookshelf Project Team',
        toolkit_link: `${window.location.origin}/corporate/toolkit`
      };
      
      Object.entries(variables).forEach(([key, value]) => {
        subject = subject.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
        body = body.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
      });
      
      setEmailPreview({ subject, body });
    }
  };

  const copyEmailTemplate = () => {
    const text = `Subject: ${emailPreview.subject}\n\n${emailPreview.body}`;
    navigator.clipboard.writeText(text);
    toast.success('Email copied to clipboard!');
  };

  const sendTemplateEmail = async () => {
    if (!selectedPartner || !emailPreview.subject || !emailPreview.body) return;
    
    try {
      const response = await fetch(`${API}/corporate/send-custom-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          corporate_id: selectedPartner.corporate_id,
          to_email: selectedPartner.contact_email,
          subject: emailPreview.subject,
          body: emailPreview.body
        })
      });

      if (response.ok) {
        toast.success(`Email sent to ${selectedPartner.contact_email}!`);
        setShowEmailDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const openActivityDialog = (partner) => {
    setSelectedPartner(partner);
    setActivityForm({ activity_type: '', description: '' });
    fetchPartnerActivities(partner.corporate_id);
    setShowActivityDialog(true);
  };

  const openPartnerDetail = (partner) => {
    setSelectedPartner(partner);
    fetchPartnerActivities(partner.corporate_id);
    setShowPartnerDetailDialog(true);
  };

  const openDeleteDialog = (partner) => {
    setSelectedPartner(partner);
    setShowDeleteDialog(true);
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner) return;
    
    try {
      const response = await fetch(`${API}/corporate/partners/${selectedPartner.corporate_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`${selectedPartner.company_name} deleted successfully`);
        setShowDeleteDialog(false);
        setSelectedPartner(null);
        fetchData();
      } else {
        toast.error('Failed to delete partner');
      }
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPartner) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be less than 5MB');
      return;
    }

    setUploadingLogo(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      
      try {
        const response = await fetch(`${API}/corporate/partners/${selectedPartner.corporate_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ company_logo: base64String })
        });

        if (response.ok) {
          toast.success('Logo uploaded successfully');
          setSelectedPartner({ ...selectedPartner, company_logo: base64String });
          fetchData();
        } else {
          toast.error('Failed to upload logo');
        }
      } catch (error) {
        toast.error('Failed to upload logo');
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    if (!selectedPartner) return;

    try {
      const response = await fetch(`${API}/corporate/partners/${selectedPartner.corporate_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company_logo: null })
      });

      if (response.ok) {
        toast.success('Logo removed');
        setSelectedPartner({ ...selectedPartner, company_logo: null });
        fetchData();
      } else {
        toast.error('Failed to remove logo');
      }
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!selectedPartner || !activityForm.activity_type || !activityForm.description) return;
    
    try {
      const response = await fetch(`${API}/corporate/partners/${selectedPartner.corporate_id}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(activityForm)
      });

      if (response.ok) {
        toast.success('Activity logged!');
        setActivityForm({ activity_type: '', description: '' });
        fetchPartnerActivities(selectedPartner.corporate_id);
        fetchData();
      } else {
        toast.error('Failed to log activity');
      }
    } catch (error) {
      toast.error('Failed to log activity');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Outreach Sent': 'bg-purple-100 text-purple-800',
      'Contacted': 'bg-amber-100 text-amber-800',
      'Confirmed': 'bg-emerald-100 text-emerald-800',
      'Completed': 'bg-slate-100 text-slate-600',
      'Sponsor': 'bg-green-100 text-green-800',
      'Long-Term Partner': 'bg-indigo-100 text-indigo-800'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const getTierBadge = (tier) => {
    const colors = {
      'Prospect': 'bg-slate-100 text-slate-600',
      'Bronze Partner': 'bg-amber-100 text-amber-800',
      'Silver Partner': 'bg-slate-200 text-slate-700',
      'Gold Partner': 'bg-yellow-100 text-yellow-800'
    };
    return <Badge className={colors[tier] || 'bg-slate-100'}>{tier}</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="corporate-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Corporate Volunteers
            </h1>
            <p className="text-slate-500 mt-1">Manage corporate partnerships and service days</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a3a5c]/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#1a3a5c]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Corporate Partners</p>
                  <p className="text-xl font-bold text-[#1a3a5c]">{stats?.total_partners || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#5cc8e8]/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#5cc8e8]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Service Days</p>
                  <p className="text-xl font-bold text-[#1a3a5c]">{stats?.total_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Corp. Volunteers</p>
                  <p className="text-xl font-bold text-[#1a3a5c]">{stats?.total_corporate_volunteers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#F4D03F]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Bookshelves Built</p>
                  <p className="text-xl font-bold text-[#1a3a5c]">{stats?.total_bookshelves_built || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sponsorship Badge */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium">Sponsorships</p>
                  <p className="text-xl font-bold text-purple-700" data-testid="sponsorship-total">
                    ${(stats?.total_sponsorship || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="partners" className="space-y-4">
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="partners">Partnership Requests</TabsTrigger>
            <TabsTrigger value="events">Service Day Events</TabsTrigger>
          </TabsList>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>Corporate Partnership Pipeline</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {partners.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Company</TableHead>
                          <TableHead className="font-semibold">Contact</TableHead>
                          <TableHead className="font-semibold">Volunteers</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Tier</TableHead>
                          <TableHead className="font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partners.map((partner) => (
                          <TableRow key={partner.corporate_id} className="table-row-hover">
                            <TableCell>
                              <div>
                                <p className="font-medium text-[#1a3a5c]">{partner.company_name}</p>
                                <p className="text-xs text-slate-500">{partner.department_name}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{partner.contact_name}</p>
                                <p className="text-xs text-slate-500">{partner.contact_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{partner.estimated_volunteers}</TableCell>
                            <TableCell>
                              <Select
                                value={partner.status}
                                onValueChange={(value) => updatePartnerStatus(partner.corporate_id, 'status', value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map((status) => (
                                    <SelectItem key={status} value={status}>{status}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={partner.tier}
                                onValueChange={(value) => updatePartnerStatus(partner.corporate_id, 'tier', value)}
                              >
                                <SelectTrigger className="w-36">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIER_OPTIONS.map((tier) => (
                                    <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPartnerDetail(partner)}
                                  data-testid={`view-partner-${partner.corporate_id}`}
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEmailDialog(partner)}
                                  data-testid={`email-partner-${partner.corporate_id}`}
                                  title="Send Email"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openActivityDialog(partner)}
                                  data-testid={`activity-partner-${partner.corporate_id}`}
                                  title="Log Activity"
                                >
                                  <History className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => sendToolkit(partner)}
                                  data-testid={`send-toolkit-${partner.corporate_id}`}
                                  title="Send Toolkit Link"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => sendInfoPackage(partner)}
                                  data-testid={`send-info-package-${partner.corporate_id}`}
                                  title="Send Info Package (PDF)"
                                  className={partner.status === 'Confirmed' ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : ''}
                                >
                                  <FileDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEventDialogForPartner(partner)}
                                  data-testid={`create-event-${partner.corporate_id}`}
                                  title="Create Event"
                                >
                                  <Calendar className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteDialog(partner)}
                                  data-testid={`delete-partner-${partner.corporate_id}`}
                                  title="Delete Partner"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No corporate partnership requests yet</p>
                    <p className="text-sm mt-2">Share your corporate volunteer page to start receiving requests</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Service Day Events</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {events.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Event</TableHead>
                          <TableHead className="font-semibold">Company</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Registered</TableHead>
                          <TableHead className="font-semibold">Impact</TableHead>
                          <TableHead className="font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {events.map((event) => (
                          <TableRow key={event.event_id} className="table-row-hover">
                            <TableCell>
                              <div>
                                <p className="font-medium text-[#1a3a5c]">{event.title}</p>
                                <p className="text-xs text-slate-500">{event.activity_type}</p>
                              </div>
                            </TableCell>
                            <TableCell>{event.company_name}</TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{event.date}</p>
                                <p className="text-xs text-slate-500">{event.start_time} - {event.end_time}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${event.registered_count >= event.volunteer_capacity ? 'text-emerald-600' : 'text-[#1a3a5c]'}`}>
                                {event.registered_count}/{event.volunteer_capacity}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p>Goal: {event.bookshelves_goal} shelves</p>
                                <p className="text-emerald-600">Built: {event.bookshelves_built}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyRegistrationLink(event.registration_token)}
                                  data-testid={`copy-link-${event.event_id}`}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Link to={`/admin/corporate/events/${event.event_id}`}>
                                  <Button variant="ghost" size="sm">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No service day events scheduled</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Event Dialog */}
        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Service Day Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={createEvent} className="space-y-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={eventForm.company_name} disabled className="bg-slate-50" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  value={eventForm.title}
                  onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="activity_type">Activity Type *</Label>
                <Select
                  value={eventForm.activity_type}
                  onValueChange={(value) => setEventForm(prev => ({ ...prev, activity_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm(prev => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_time">Start *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={eventForm.start_time}
                    onChange={(e) => setEventForm(prev => ({ ...prev, start_time: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time">End *</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={eventForm.end_time}
                    onChange={(e) => setEventForm(prev => ({ ...prev, end_time: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={eventForm.location}
                  onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="volunteer_capacity">Volunteer Capacity</Label>
                  <Input
                    id="volunteer_capacity"
                    type="number"
                    value={eventForm.volunteer_capacity}
                    onChange={(e) => setEventForm(prev => ({ ...prev, volunteer_capacity: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Est. Bookshelves</Label>
                  <Input
                    value={Math.round(eventForm.volunteer_capacity * 1.5)}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEventDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90">
                  Create Event & Get Link
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Email Template Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send Email to {selectedPartner?.company_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an email template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(emailTemplates).map(([id, template]) => (
                      <SelectItem key={id} value={id}>{template.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {emailPreview.subject && (
                <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                  <div>
                    <Label className="text-xs text-slate-500">To</Label>
                    <p className="text-sm font-medium">{selectedPartner?.contact_email}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Subject</Label>
                    <Input
                      value={emailPreview.subject}
                      onChange={(e) => setEmailPreview(prev => ({ ...prev, subject: e.target.value }))}
                      className="font-medium"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Body (editable)</Label>
                    <Textarea
                      value={emailPreview.body}
                      onChange={(e) => setEmailPreview(prev => ({ ...prev, body: e.target.value }))}
                      className="min-h-[200px] text-sm bg-white"
                      rows={12}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="outline" 
                  onClick={copyEmailTemplate}
                  disabled={!emailPreview.subject}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  onClick={sendTemplateEmail}
                  disabled={!emailPreview.subject || !emailPreview.body}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Activity Log Dialog */}
        <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Activity Log - {selectedPartner?.company_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Add Activity Form */}
              <form onSubmit={addActivity} className="space-y-3 border-b pb-4">
                <div className="space-y-2">
                  <Label>Activity Type</Label>
                  <Select 
                    value={activityForm.activity_type} 
                    onValueChange={(value) => setActivityForm(prev => ({ ...prev, activity_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_LOG_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Enter activity details..."
                    value={activityForm.description}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  disabled={!activityForm.activity_type || !activityForm.description}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Log Activity
                </Button>
              </form>

              {/* Activity History */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-500">Activity History</Label>
                {partnerActivities.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {partnerActivities.map((activity) => {
                      const typeInfo = ACTIVITY_LOG_TYPES.find(t => t.value === activity.activity_type);
                      const Icon = typeInfo?.icon || StickyNote;
                      return (
                        <div key={activity.activity_id} className="flex items-start gap-3 p-2 bg-slate-50 rounded text-sm">
                          <Icon className="w-4 h-4 mt-0.5 text-slate-500" />
                          <div className="flex-1">
                            <p className="font-medium">{typeInfo?.label || activity.activity_type}</p>
                            <p className="text-slate-600">{activity.description}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">No activities logged yet</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Partner Detail Dialog */}
        <Dialog open={showPartnerDetailDialog} onOpenChange={setShowPartnerDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPartner?.company_name}</DialogTitle>
            </DialogHeader>
            {selectedPartner && (
              <div className="space-y-6">
                {/* Status and Tier */}
                <div className="flex gap-4">
                  {getStatusBadge(selectedPartner.status)}
                  {getTierBadge(selectedPartner.tier)}
                  {selectedPartner.toolkit_sent && (
                    <Badge className="bg-green-100 text-green-800">Toolkit Sent</Badge>
                  )}
                  {selectedPartner.logo_usage_consent && (
                    <Badge className="bg-purple-100 text-purple-800">Logo Use Approved</Badge>
                  )}
                </div>

                {/* Company Logo Section */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" /> Company Logo
                    </h4>
                    {selectedPartner.company_logo && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={removeLogo}
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  
                  {selectedPartner.company_logo ? (
                    <div className="flex items-center gap-4">
                      <img 
                        src={selectedPartner.company_logo} 
                        alt={`${selectedPartner.company_name} logo`}
                        className="max-h-20 max-w-40 object-contain rounded border bg-white p-2"
                      />
                      <div className="text-sm text-slate-500">
                        {selectedPartner.logo_usage_consent ? (
                          <span className="text-green-600">✓ Approved for co-branding & marketing</span>
                        ) : (
                          <span className="text-amber-600">⚠ No usage consent provided</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-[#5cc8e8] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="partner-logo-upload"
                        data-testid="partner-logo-input"
                      />
                      <label 
                        htmlFor="partner-logo-upload" 
                        className="cursor-pointer flex flex-col items-center"
                      >
                        {uploadingLogo ? (
                          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin mb-2" />
                        ) : (
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        )}
                        <span className="text-sm text-slate-600">
                          {uploadingLogo ? 'Uploading...' : 'Click to upload company logo'}
                        </span>
                        <span className="text-xs text-slate-400 mt-1">PNG, JPG, SVG up to 5MB</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Company Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Company Information</h4>
                    <div>
                      <p className="text-xs text-slate-500">Company Name</p>
                      <p className="text-sm font-medium">{selectedPartner.company_name}</p>
                    </div>
                    {selectedPartner.department_name && (
                      <div>
                        <p className="text-xs text-slate-500">Department</p>
                        <p className="text-sm">{selectedPartner.department_name}</p>
                      </div>
                    )}
                    {selectedPartner.industry && (
                      <div>
                        <p className="text-xs text-slate-500">Industry</p>
                        <p className="text-sm">{selectedPartner.industry}</p>
                      </div>
                    )}
                    {selectedPartner.employee_count && (
                      <div>
                        <p className="text-xs text-slate-500">Employee Count</p>
                        <p className="text-sm">{selectedPartner.employee_count}</p>
                      </div>
                    )}
                    {selectedPartner.company_website && (
                      <div>
                        <p className="text-xs text-slate-500">Website</p>
                        <a href={selectedPartner.company_website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#5cc8e8] hover:underline">
                          {selectedPartner.company_website}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Contact Information</h4>
                    <div>
                      <p className="text-xs text-slate-500">Contact Name</p>
                      <p className="text-sm font-medium">{selectedPartner.contact_name}</p>
                    </div>
                    {selectedPartner.contact_title && (
                      <div>
                        <p className="text-xs text-slate-500">Title</p>
                        <p className="text-sm">{selectedPartner.contact_title}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm">{selectedPartner.contact_email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Phone</p>
                      <p className="text-sm">{selectedPartner.contact_phone}</p>
                    </div>
                    {selectedPartner.contact_linkedin && (
                      <div>
                        <p className="text-xs text-slate-500">LinkedIn</p>
                        <a href={selectedPartner.contact_linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-[#5cc8e8] hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Volunteer Details */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Volunteer Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Est. Volunteers</p>
                      <p className="text-sm font-medium">{selectedPartner.estimated_volunteers}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Preferred Date</p>
                      <p className="text-sm">{selectedPartner.preferred_date_range}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Preferred Time</p>
                      <p className="text-sm">{selectedPartner.preferred_time}</p>
                    </div>
                  </div>
                  {selectedPartner.preferred_activities?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Preferred Activities</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedPartner.preferred_activities.map((activity, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{activity}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sponsorship */}
                {(selectedPartner.interested_in_sponsorship || selectedPartner.has_volunteer_grant) && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Sponsorship & Grants</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedPartner.interested_in_sponsorship && (
                        <div>
                          <p className="text-xs text-slate-500">Sponsorship Interest</p>
                          <p className="text-sm">Yes {selectedPartner.sponsorship_level && `- ${selectedPartner.sponsorship_level}`}</p>
                          {selectedPartner.sponsorship_amount && (
                            <p className="text-sm font-medium text-green-600">${selectedPartner.sponsorship_amount.toLocaleString()}</p>
                          )}
                        </div>
                      )}
                      {selectedPartner.has_volunteer_grant && (
                        <div>
                          <p className="text-xs text-slate-500">Volunteer Grant</p>
                          <p className="text-sm">Available {selectedPartner.volunteer_grant_amount && `- $${selectedPartner.volunteer_grant_amount.toLocaleString()}`}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tracking Dates */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Tracking</h4>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-xs text-slate-500">Outreach Sent</p>
                      <p className="text-sm font-medium">{selectedPartner.outreach_sent_date || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-xs text-slate-500">Last Follow-up</p>
                      <p className="text-sm font-medium">{selectedPartner.follow_up_date || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-xs text-slate-500">Last Contact</p>
                      <p className="text-sm font-medium">{selectedPartner.last_contact_date || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-xs text-slate-500">Toolkit Sent</p>
                      <p className="text-sm font-medium">{selectedPartner.toolkit_sent_at ? new Date(selectedPartner.toolkit_sent_at).toLocaleDateString() : '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                {partnerActivities.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Recent Activity</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {partnerActivities.slice(0, 5).map((activity) => {
                        const typeInfo = ACTIVITY_LOG_TYPES.find(t => t.value === activity.activity_type);
                        return (
                          <div key={activity.activity_id} className="flex items-center gap-2 text-sm">
                            <span className="text-xs text-slate-400 w-20">{new Date(activity.created_at).toLocaleDateString()}</span>
                            <Badge variant="outline" className="text-xs">{typeInfo?.label || activity.activity_type}</Badge>
                            <span className="text-slate-600 truncate">{activity.description}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedPartner.notes && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-[#1a3a5c] border-b pb-1">Notes</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{selectedPartner.notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => openEmailDialog(selectedPartner)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button variant="outline" onClick={() => { sendInfoPackage(selectedPartner); setShowPartnerDetailDialog(false); }}>
                    <FileDown className="w-4 h-4 mr-2" />
                    Send Info Package
                  </Button>
                  <Button variant="outline" onClick={() => { setShowPartnerDetailDialog(false); openActivityDialog(selectedPartner); }}>
                    <History className="w-4 h-4 mr-2" />
                    Log Activity
                  </Button>
                  <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" onClick={() => { setShowPartnerDetailDialog(false); openEventDialogForPartner(selectedPartner); }}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Partner Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Corporate Partner</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to permanently delete{' '}
                <strong>{selectedPartner?.company_name}</strong>?
              </p>
              <p className="text-sm text-red-600">
                This action cannot be undone. All data associated with this partner will be removed.
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeletePartner}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-partner-btn"
                >
                  Delete Partner
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
